import { useMemo } from 'react';

import type { LinkProps } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Card } from '@/_components';
import { nameResolver } from '@/_shared';

import { FilterDropdown } from './filter-dropdown';
import { PriorityIndicator } from './priority-indicator';
import { TicketMetadata } from './ticket-metadata';
import { TicketStatusBadge } from './ticket-status-badge';

import { PriorityBadge } from '../../shared/components';
import { isTicketOpen, isTicketOverdue } from '../../shared/helper';
import type { SupportModule, SupportTicketSummary } from '../../shared/schemas/support-query-schema';
import { priorityOptions, supportStatusOptions } from '../../shared/schemas/support-query-schema';

type TicketListItemProps = {
  ticket: SupportTicketSummary;
  module: SupportModule;
  moduleId: string;
};

export function TicketListItem({ ticket, module, moduleId }: TicketListItemProps) {
  const routeTo: LinkProps['to'] = useMemo(() => {
    if (module === 'companies') {
      return `/companies/$id/support/$ticket_id` as LinkProps['to'];
    }
    if (module === 'contacts') {
      return `/contacts/$id/support/$ticket_id` as LinkProps['to'];
    }
    return `/support/$ticket_id` as LinkProps['to'];
  }, [module]);

  const isClosed = !isTicketOpen(ticket.status);

  return (
    <Card
      className={twMerge(
        'flex flex-row items-center gap-4 rounded-none bg-white px-4 py-3 hover:bg-gray-50 dark:bg-gray-950 hover:dark:bg-gray-800',
        isClosed ? 'opacity-60 grayscale' : ''
      )}
    >
      <Link
        to={routeTo}
        // @ts-expect-error - TODO: fix the type error
        params={{ id: moduleId, ticket_id: ticket.id.toString() }}
        className="flex flex-1 flex-row items-center gap-4"
      >
        {/* Avatar */}
        <PriorityIndicator
          priority={ticket.priority}
          requesterName={nameResolver(ticket.requester?.first_name, ticket.requester?.last_name, '***UNKNOWN***')}
          requesterAvatar={ticket.requester?.profile_image?.[0]?.url}
        />

        {/* Main Content */}
        <div className="min-w-0 flex-1 space-y-1.5">
          <TicketStatusBadge status={ticket.status} isOverdue={isTicketOverdue(ticket.status, ticket.due_by)} />

          <h3 className="text-c-card-foreground flex flex-row items-center gap-1.5 text-sm leading-5 font-medium">
            {ticket.subject} <span className="text-c-card-muted text-xs">#{ticket.id}</span>
          </h3>
          <TicketMetadata
            ticketStatus={ticket.status}
            requesterName={nameResolver(ticket.requester?.first_name, ticket.requester?.last_name, '***UNKNOWN***')}
            companyName={ticket.company?.name}
            dueBy={ticket.due_by}
          />
        </div>
      </Link>

      {/* Right Column */}
      <div className="flex min-w-[120px] shrink-0 flex-col items-end gap-0.5 text-right">
        <FilterDropdown
          value={priorityOptions.find((option) => option.value.toString() == ticket.priority.toString()) || undefined}
          options={priorityOptions}
          disabled
          placeholder="Priority"
          getOptionLabel={(option) => (
            <PriorityBadge priority={option.value as unknown as number} variant="box" boxClassName="text-xs" />
          )}
        />
        <FilterDropdown
          value={{
            value: ticket?.agent?.id?.toString() || 'unassigned',
            label: nameResolver(ticket?.agent?.first_name, ticket?.agent?.last_name, '***UNKNOWN***'),
          }}
          options={[
            {
              value: ticket?.agent?.id?.toString() || 'unassigned',
              label: nameResolver(ticket?.agent?.first_name, ticket?.agent?.last_name, '***UNKNOWN***'),
            },
          ]}
          disabled
          placeholder="Agent"
          showIcon="user"
        />
        <FilterDropdown
          value={
            supportStatusOptions.find((option) => option.value.toString() == ticket.status.toString()) || undefined
          }
          options={supportStatusOptions}
          disabled
          placeholder="Status"
          showIcon="status"
        />
      </div>
    </Card>
  );
}
